import styles from './CampaignName.module.css';
import { TooltipOnOverflow, assets } from '@round/whitelabel-ui';
import { useState } from 'react';
import { Input } from '@round/whitelabel-ui';
import { showNotification } from 'helpers';
import { combine, onKey, stopPropagation } from 'utility/events';
import { creatorbase } from '@round/api';
import { Spinner } from '@round/whitelabel-ui';
import { Tooltip } from '@round/whitelabel-ui';
import { useInvalidateCampaigns } from '@round/creatorbase-helpers/api-hooks';

const { Pencil: PencilIcon } = assets;

type Props = {
    name: string | null | undefined;
    campaignId?: number;
    isReadonly?: boolean;
    updateName?: (
        name: string
    ) => ReturnType<typeof creatorbase.patchCampaign | typeof creatorbase.patchCampaignShare> | undefined;
};

const CampaignName = ({ isReadonly, campaignId, name, updateName }: Props) => {
    const [campaignName, setCampaignName] = useState(name || '');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const invalidateCampaigns = useInvalidateCampaigns();

    const submitCampaignName = async (newName: string) => {
        const DEFAULT_ERROR_MESSAGE = 'Could not update campaign name';
        try {
            setIsUpdateLoading(true);
            const response = await updateName?.(newName);
            if (response?.status === 200) {
                invalidateCampaigns(campaignId);
                showNotification('Campaign name updated', 'info');
                return;
            }

            setCampaignName(name || '');
            if (!response) {
                showNotification(DEFAULT_ERROR_MESSAGE, 'error');
                return;
            }

            const message =
                response.status === 404 || response.status === 403
                    ? response.data.detail
                    : response.data.name ?? DEFAULT_ERROR_MESSAGE;
            showNotification(String(message), 'error');
        } catch {
            setCampaignName(name || '');
            showNotification(DEFAULT_ERROR_MESSAGE, 'error');
        } finally {
            setIsUpdateLoading(false);
        }
    };

    if (isEditMode && !isReadonly) {
        return (
            <Input
                autoFocus
                containerClassName={styles.inputContainer}
                className={styles.input}
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                onClick={stopPropagation}
                onBlur={() => setIsEditMode(false)}
                onKeyDown={combine(
                    onKey('Enter', (e) => {
                        if (campaignName === name) {
                            e.currentTarget.blur();
                            return;
                        }

                        submitCampaignName(campaignName);
                    }),
                    onKey('Escape', (e) => {
                        setCampaignName(name || '');
                        e.currentTarget.blur();
                    })
                )}
                renderRight={() => {
                    if (!isUpdateLoading) {
                        return null;
                    }

                    return <Spinner />;
                }}
            />
        );
    }

    const ignoreOnReadonly = <T extends Function>(func: T) => (isReadonly ? undefined : func);
    return (
        <div className={styles.container}>
            <TooltipOnOverflow tooltip={<p className={styles.tooltip}>{campaignName}</p>}>
                <p
                    className={styles.campaignName}
                    onClick={ignoreOnReadonly(stopPropagation)}
                    onDoubleClick={ignoreOnReadonly(() => setIsEditMode(true))}
                >
                    {campaignName || '-'}
                </p>
            </TooltipOnOverflow>

            {!isReadonly && (
                <Tooltip.Root>
                    {(anchor, setAnchor) => (
                        <>
                            <PencilIcon
                                ref={setAnchor}
                                className={styles.editIcon}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditMode(true);
                                }}
                            />

                            <Tooltip anchorElement={anchor}>
                                <Tooltip.Body>
                                    <span>Click on icon or double-click on campaign name to edit</span>
                                </Tooltip.Body>
                            </Tooltip>
                        </>
                    )}
                </Tooltip.Root>
            )}
        </div>
    );
};

export default CampaignName;
